import _ from 'lodash';
import React, { useCallback, useContext, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { useTranslator } from '@jutro/locale';
import { useModal } from '@jutro/components';
import { useAuthentication, withAuthenticationContext } from '@xengage/gw-digital-auth-react';
import { BreakpointTrackerContext } from '@jutro/layout'
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import TWIADateFormatUtil from "ent-capability-quoteandbind-common-react/util/TWIADateFormatUtil";
// import PrintDocumentUtil from 'ent-capability-quoteandbind-common-react/util/PrintDocumentUtil.js';
import { Link } from '@jutro/router';
import metadata from './TWIASuccessPage.metadata.json5';
import styles from './TWIASuccessPage.module.scss';
import messages from './TWIASuccessPage.messages.js';

function TWIASuccessfulPage(props) {

    const { location } = props;
    const submissionVM = location.state.response;
    const autHeader = location.state.authHeader;
    const breakpoint = useContext(BreakpointTrackerContext);
    const isPolicyBound = _.get(submissionVM, 'baseData.periodStatus') === 'Bound';
    const translator = useTranslator();
    const viewModelService = useContext(ViewModelServiceContext);
    const { showAlert, showConfirm } = useModal();
    const [isLoading, setIsLoading] = useState(false);
    const eftEnabled = _.get(submissionVM, 'bindData.eftEnabled_TWIA');
    const quoteID = _.get(submissionVM, 'quoteID');
    const policyNumber = _.get(submissionVM, 'bindData.policyNumber');
    const documentPath = `${process.env.PUBLIC_PATH}/quotes/${quoteID}/documents`
    const internalPath = `${process.env.PUBLIC_PATH}/quotes/${quoteID}/summary`
    const redirectPath = `${process.env.PUBLIC_PATH}/policies/${policyNumber}/summary`
    const chosenQuote = _.find(submissionVM?.quoteData?.offeredQuotes, {
        publicID: submissionVM.bindData?.chosenQuote
    });
    const chosenPlan = _.find(submissionVM.bindData.paymentPlans, {
        billingId: submissionVM.bindData?.selectedPaymentPlan
    });
    const primaryNamedInsured = _.find(submissionVM.baseData.policyContactDetails_TWIA, { policyContactRoles: [{ subtype: 'PolicyPriNamedInsured' }] });

    const contactNumber = useCallback(() => {
        const insuredDetails = _.find(submissionVM.baseData.policyContactDetails_TWIA, { policyContactRoles: [{ subtype: 'PolicyPriNamedInsured' }] });
        const contact = formatPhoneNumber(insuredDetails);

        if (contact) {
            return contact;
        }

        return null;
    }, []);


    const formatPhoneNumber = (contact) => {
        const number = getPrimaryPhone(contact);

        if (number === undefined || number === null) {
            return null;
        }

        return number;
    };

    const getPrimaryPhone = (contact) => {
        switch (contact.primaryPhoneType) {
            case 'home':
                return contact.homeNumber;
            case 'work':
                return contact.workNumber;
            case 'mobile':
                return contact.cellNumber;
            default:
                return null;
        }
    }

    const getAddressLineFields = useCallback(() => {
        if (
            submissionVM.lobData.homeowners.dwellingLocation_TWIA
        ) {
            return (
                <div>
                    <p>
                        {submissionVM.lobData.homeowners.dwellingLocation_TWIA.addressLine1}
                    </p>
                    <p>
                        {submissionVM.lobData.homeowners.dwellingLocation_TWIA.city}
                    </p>
                    <p>
                        {submissionVM.lobData.homeowners.dwellingLocation_TWIA.state}
                    </p>
                    <p>
                        {submissionVM.lobData.homeowners.dwellingLocation_TWIA.postalCode}
                    </p>
                </div>
            );
        }

        return '';
    }, [submissionVM]);

    const quotePrintDocument = useCallback(async () => {
        window.print();
    }, [])

    const getOfferNumber = useCallback(() => {
        return (
            <Link to={internalPath}>
                {quoteID}
            </Link>
        );
    }, []);

    const getPolicyNumber = useCallback(() => {
        return (
            <Link to={redirectPath}>
                {policyNumber}
            </Link>
        );
    }, [policyNumber]);

    const paymentMethod = viewModelService?.productMetadata?.get('pc').types.getTypelist('PaymentMethod_TWIA');

    // DE17459
    const getSelectedPaymentMethod = useCallback(() => (!eftEnabled
        // eslint-disable-next-line no-secrets/no-secrets
        ? _.get(submissionVM, `bindData.paymentDetails.nonEFTPaymentMethod_TWIA`)
        // eslint-disable-next-line no-secrets/no-secrets
        : _.get(submissionVM, `bindData.paymentDetails.paymentMethod_TWIA`)),
        [eftEnabled, submissionVM]);

    const selectPayment = useCallback(() => {
        const method = getSelectedPaymentMethod();

        if (paymentMethod !== undefined) {
            const result = paymentMethod?.codes?.filter(item => {
                if (item.code === method) {
                    return item;
                }
            })
            
            // DE16752
            return translator({
                id: result[0].name,
                defaultMessage: result[0].code
            })
        }
    }, [getSelectedPaymentMethod, paymentMethod, translator]);


    // DE16549
    const periodEndDate = _.get(submissionVM, `baseData.periodEndDate`);
    const periodStartDate = _.get(submissionVM, `baseData.periodStartDate`);

    const formatPolicyPeriod = (startDate, endDate) => {
        const formattedStartDate = TWIADateFormatUtil.formatDate(startDate);
        const formattedEndDate = TWIADateFormatUtil.formatDate(endDate);

        return `${formattedStartDate} - ${formattedEndDate}`;
    };

    const overrides = {
        '@field': {
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
            readOnly: true
        },
        applicationReceived: {
            visible: !isPolicyBound,
        },
        policyIssued: {
            visible: isPolicyBound
        },
        policyContent: {
            visible: !isPolicyBound,
            content: translator(messages.applicationContent)
        },
        isssuedPolicyContent: {
            visible: isPolicyBound,
            content: translator(messages.issuedApplicationContent)
        },
        totalPremium: {
            value: {
                amount: _.get(submissionVM, 'quoteData.offeredQuotes[0].premium.total.amount'),
                currency: "USD"
            }
        },
        requestedCoverageStartDate: {
            label: isPolicyBound ? ' Policy Effective Date' : 'Requested Coverage Start Date',
        },
        proposedPolicyPeriod: {
            label: isPolicyBound ? 'Policy Period' : 'Proposed Policy Period',
            value: formatPolicyPeriod(periodStartDate, periodEndDate) // DE16549
        },
        addressField: {
            value: getAddressLineFields(),
        },
        paymentPlan: {
            value: chosenPlan?.name
        },
        name: {
            visible: primaryNamedInsured.subtype === 'Company',
            value: primaryNamedInsured.displayName
        },
        firstName: {
            visible: primaryNamedInsured.subtype === 'Person',
            value: primaryNamedInsured.firstName
        },
        lastName: {
            visible: primaryNamedInsured.subtype === 'Person',
            value: primaryNamedInsured.lastName
        },
        emailAddress: {
            value: primaryNamedInsured.emailAddress1
        },
        phoneNumber: {
            value: contactNumber()
        },
        offerNumber: {
            visible: !isPolicyBound,
            value: getOfferNumber()
        },
        confirmationNumber: {
            visible: isPolicyBound && submissionVM.bindData.paymentDetails.confirmationNumber_TWIA !== undefined && submissionVM.bindData.paymentDetails.downPaymentMethod_TWIA !== 'ins_direct_pay'
        },
        downPaymenMethod: {
            visible: isPolicyBound && submissionVM.bindData.paymentDetails.downPaymentMethod_TWIA !== undefined
        },
        policyNumber: {
            visible: isPolicyBound,
            value: getPolicyNumber()
        },
        paymentMethodWithEft: {
            visible: eftEnabled,
            value : selectPayment()
        },
        paymentMethod: {
            visible: !eftEnabled,
            value : selectPayment()
        },
        fileExtensionLink: {
            to: documentPath
        }

    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            quotePrintDocument
        },
    };

    return (
        <ViewModelForm
                uiProps={metadata.pageContent}
                model={submissionVM}
                // onModelChange={updateWizardData}
                overrideProps={overrides}
                classNameMap={resolvers.resolveClassNameMap}
                callbackMap={resolvers.resolveCallbackMap}
        />
    );
}

TWIASuccessfulPage.propTypes = wizardProps;

export default withRouter(TWIASuccessfulPage);